import { LeftOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Drawer, Form } from 'antd';
import Button from 'antd/es/button';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { VoucherBody } from 'services/api/voucher';
import { useVoucherDetailShow } from 'hooks/business/voucherDetail';
import { voucherVo2FormValuesAdapter } from './utils';
import useVoucherValues from './useVoucherValues';
import { useMemoizedFn } from 'ahooks';
import { useVoucherFormSubmit } from './useVoucherFormSubmit';
import { VoucherFormBody } from './VoucherFormBody';

const { useForm } = Form;

const Header = styled.div`
  position: relative;
  height: 48px;
  line-height: 48px;
  font-weight: 500;
  text-align: center;
  box-shadow: inset 0 -1px 0 0 rgba(225, 230, 238, 1);
`;
const HeaderBack = styled(Button)`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
`;

export interface VoucherDrawerProps {
  conversation: Conversation.Conversation;
  visible: boolean;
  title?: string;
  confirmText?: string;
  onCancel: (status?: Conversation.Voucher.Status) => void;
  afterSubmit?: () => void;
}

interface ExtraFormValues extends Conversation.Voucher.Vo {
  displayChannel: number;
}

const VoucherDrawer = (props: VoucherDrawerProps, ref: any) => {
  const { t } = useTranslate();
  const voucherDetailShow = useVoucherDetailShow();

  const {
    conversation,
    visible,
    title = t('chatWorkspace.couponForm.title.create'),
    confirmText = t('common.action.confirm'),
    onCancel,
    afterSubmit,
  } = props;
  const { storeId, channel } = conversation;
  const [form] = useForm<VoucherBody>();
  const [status, setStatus] = useState<Conversation.Voucher.Status | undefined>();
  const [voucherValues, setVoucherValues] = useState<ExtraFormValues>();
  const { clearVoucherFormCache } = useVoucherValues();
  const { dynamicRules, submitLoading, submitCreateFormValues, submitEditFormValues } =
    useVoucherFormSubmit();

  const submitVoucher = useMemoizedFn(async (formValues) => {
    if (status && voucherValues) {
      // 编辑优惠券
      const result = await submitEditFormValues(
        voucherValues.internalVoucherId,
        formValues,
        storeId,
        channel,
      );
      if (result) {
        clearVoucherFormCache(storeId);
        onCancel && onCancel();
        afterSubmit && afterSubmit();
      }
    } else {
      // 添加优惠券
      const result = await submitCreateFormValues(formValues, storeId, channel);
      if (result) {
        clearVoucherFormCache(storeId);
        onCancel && onCancel('ALL');
        afterSubmit && afterSubmit();
      }
    }
  });

  const setFormValues = (values: Conversation.Voucher.Vo) => {
    form?.resetFields();
    const formValues = voucherVo2FormValuesAdapter(values);
    // @ts-ignore
    setVoucherValues(formValues);
    // @ts-ignore
    form?.setFieldsValue(formValues);
  };

  useImperativeHandle(ref, () => ({
    form,
    setFormValues,
    setStatus,
  }));

  useEffect(() => {
    if (!visible) {
      setVoucherValues(undefined);
    }
  }, [visible]);

  useEffect(() => {
    setTimeout(() => {
      form?.validateFields();
    }, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicRules]);

  return visible ? (
    <Drawer
      visible={visible}
      mask={false}
      getContainer={false}
      closable={false}
      bodyStyle={{
        position: 'relative',
        padding: 0,
        overflow: 'hidden',
      }}
      style={{
        width: 'var(--voucher-drawer-width, 420px)',
        position: 'absolute',
      }}
      zIndex={11}
    >
      <Header>
        <HeaderBack type="link" icon={<LeftOutlined />} onClick={() => onCancel && onCancel()}>
          {t('common.action.back')}
        </HeaderBack>
        {title}
      </Header>
      <VoucherFormBody
        conversation={conversation}
        form={form}
        status={status}
        submitVoucher={submitVoucher}
        submitLoading={submitLoading}
        confirmText={confirmText}
        dynamicRules={dynamicRules}
        submitButtonDisabled={voucherDetailShow}
      />
    </Drawer>
  ) : null;
};

export default forwardRef(VoucherDrawer);
